import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { CommunityLanguageCheckResponse } from '../models/community.models';
import { CommunityService } from './community.service';

/** What the writer picked in the dialog. */
export type CommunityLanguageChoice = 'detected' | 'expected' | 'cancel';

/** The open question, read by `CommunityLanguageConfirmModalComponent`. */
export interface CommunityLanguageCheckRequest {
  /** The language the model thinks the draft is written in. */
  detected: string;
  /** The language it would otherwise be stored as. */
  expected: string;
}

/** What a save path does next. `locale` is only set when the writer confirmed one. */
export interface CommunityLanguageDecision {
  proceed: boolean;
  locale?: string;
}

/**
 * The client half of §7.3: before a thread or post is saved, ask the server what
 * language it looks like, and only when it confidently disagrees with what the
 * record would be stored as, put the question to the writer.
 *
 * Everything about this is fail-open. The endpoint may be missing (the feature
 * ships in two halves), the model may be down, the answer may be slow — in every
 * one of those cases the draft is saved unchanged, exactly as before. A language
 * guess must never be able to swallow somebody's post, so there is no error path
 * that stops a save and none that shows the writer a failure.
 *
 * The dialog itself cannot be opened from here: a root service renders nothing.
 * Instead `request` holds the open question and the two pages host
 * `<app-community-language-confirm-modal />` once, which reads that signal and
 * reports the answer back through `resolve`.
 */
@Injectable({ providedIn: 'root' })
export class CommunityLanguageCheckService {
  /** The server is asked to answer inside this window; after it, the save goes ahead. */
  private static readonly TIMEOUT_MS = 10_000;

  private readonly community = inject(CommunityService);

  /** Non-null while the dialog is open. */
  readonly request = signal<CommunityLanguageCheckRequest | null>(null);

  private pending: ((choice: CommunityLanguageChoice) => void) | null = null;

  /**
   * Resolves once the save may go ahead. `locale` is the language the writer
   * confirmed; when it is absent the caller saves exactly as it would have.
   */
  async confirmLocale(input: {
    title?: string;
    body: string;
    threadId?: number;
    /** A new thread: the channel it is written into (a Private Room skips the model while its switch is off). */
    channelId?: number;
    expected?: string;
  }): Promise<CommunityLanguageDecision> {
    let answer: CommunityLanguageCheckResponse;
    try {
      answer = await firstValueFrom(
        this.community
          .languageCheck({
            body: input.body,
            ...(input.title ? { title: input.title } : {}),
            ...(input.threadId != null ? { thread_id: input.threadId } : {}),
            ...(input.channelId != null ? { channel_id: input.channelId } : {}),
            ...(input.expected ? { locale: input.expected } : {})
          })
          .pipe(timeout(CommunityLanguageCheckService.TIMEOUT_MS))
      );
    } catch {
      return { proceed: true };
    }

    if (!answer?.available || !answer.mismatch || !answer.detected || !answer.expected) {
      return { proceed: true };
    }

    const choice = await this.ask({ detected: answer.detected, expected: answer.expected });
    if (choice === 'cancel') return { proceed: false };
    return { proceed: true, locale: choice === 'detected' ? answer.detected : answer.expected };
  }

  /** Called by the modal component; closes the dialog and releases `confirmLocale`. */
  resolve(choice: CommunityLanguageChoice): void {
    const pending = this.pending;
    this.pending = null;
    this.request.set(null);
    pending?.(choice);
  }

  private ask(request: CommunityLanguageCheckRequest): Promise<CommunityLanguageChoice> {
    // A second submit while a dialog is open cancels the first one rather than
    // orphaning its promise.
    this.resolve('cancel');
    return new Promise<CommunityLanguageChoice>(resolve => {
      this.pending = resolve;
      this.request.set(request);
    });
  }
}
